<template>
  <div class="row">
    <div class="col-12">
      <page-header :page="page" />
    </div>
    <div v-if="showFavoriteSection" class="col-12">
      <div class="d-flex justify-content-between align-items-center">
        <section-title
          class="favorite-title"
          bg-icon="white"
          icon="fas fa-star text-warning"
          content="Favoritos"
          container-class="mt-4 ml-4"
        />
        <div v-if="menuAdvanceModeEnabled" class="view-toggle mr-4 mt-4">
          <button
            type="button"
            class="view-toggle-btn"
            :class="{ active: viewMode === 'list' }"
            @click="setViewMode('list')"
          >
            <i class="fas fa-list"></i>
          </button>
          <button
            type="button"
            class="view-toggle-btn"
            :class="{ active: viewMode === 'grid' }"
            @click="setViewMode('grid')"
          >
            <i class="fas fa-th-large"></i>
          </button>
        </div>
      </div>
      <div v-if="optionFavoriteList.length > 0" :class="optionContainerClass">
        <square-option
          v-for="option in optionFavoriteList"
          :key="option.id"
          :option="option"
          :view-mode="effectiveViewMode"
          show-star
          @remove="removeToFavorites(option)"
        />
      </div>
      <div v-else class="d-block ml-auto mr-auto mb-2">
        <empty
          text="Puede oprimir la estrella en las tarjetas para agregarlas como favoritos."
          icon="fal fa-lightbulb-exclamation"
        />
      </div>
      <div id="border"></div>
    </div>
    <div :class="optionContainerClass">
      <square-option
        v-for="option in optionList"
        :key="option.id"
        :option="option"
        :view-mode="effectiveViewMode"
        :show-star="menuAdvanceModeEnabled"
        @add="addToFavorites(option)"
        @remove="removeToFavorites(option)"
      />
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import PageHeader from 'PageHeader'
import SquareOption from '@/components/common/menu/SquareOption.vue'
import SectionTitle from 'SectionTitle'
import { Empty } from '@/components/common'
import SpecialService from '@/services/api/special.api'
import { addNavToFavorites, removeNavToFavorites } from '@/core/auth'
import { getUserMenu } from '@/modules/user/api'
import { name as userModuleName } from '@/modules/user/store'
import { name as commonModuleName } from '@/modules/common/store'
import navMenuTitles from '@/utils/constants/navMenuTitles'
import SysParameters from '@/utils/constants/sysParameters'

const VIEW_MODE_KEY = 'menuViewMode'
const VIEW_MODES = ['grid', 'list']

export default {
  components: {
    SquareOption,
    PageHeader,
    SectionTitle,
    Empty,
  },
  data() {
    return {
      page: {
        icon: 'far fa-file-chart-line mx-1',
        title: '618 Data',
        routes: ['Inicio', '618 Data'],
      },
      menuAdvanceModeEnabled: false,
      viewMode: localStorage.getItem(VIEW_MODE_KEY) || 'grid',
    }
  },
  computed: {
    ...mapState(userModuleName, ['userMenu']),
    ...mapGetters(commonModuleName, ['getFilterText']),
    menuOptions() {
      return (
        this.userMenu
          .find((x) => x.title === navMenuTitles.Eden)
          ?.children.map((item) => ({
            id: item.id,
            title: item.title,
            description: item.description,
            detail: item.detail,
            icon: item.iconClasses,
            mainImage: item.mainImagePath,
            hoverImage: item.mainImageHoverPath,
            needSelectedStudent: item.needSelectedStudent,
            route: item.navigationPath,
            isFavorite: item.isFavorite,
          })) || []
      )
    },
    optionList() {
      const items = this.menuOptions

      const filteredItems = this.getFilterText
        ? items?.filter((e) =>
            e.title.toLowerCase().match(this.getFilterText.toLowerCase())
          )
        : items

      return filteredItems
    },
    optionFavoriteList() {
      return this.menuOptions.filter((x) => x.isFavorite)
    },
    showFavoriteSection() {
      return this.menuAdvanceModeEnabled
    },
    effectiveViewMode() {
      return this.menuAdvanceModeEnabled ? this.viewMode : 'grid'
    },
    optionContainerClass() {
      return this.effectiveViewMode === 'list'
        ? 'col-12 menu-list-container mt-0 mb-3'
        : 'col-12 flip-cards-container mt-0 mb-5'
    },
  },
  async mounted() {
    this.menuAdvanceModeEnabled = await this.isMenuAdvanceModeEnabled()
  },
  methods: {
    ...mapActions(commonModuleName, ['setFilterText']),
    ...mapActions(userModuleName, ['setMenu']),
    setViewMode(mode) {
      if (!VIEW_MODES.includes(mode)) {
        return
      }
      this.viewMode = mode
      localStorage.setItem(VIEW_MODE_KEY, mode)
    },
    async isMenuAdvanceModeEnabled() {
      const { data } = await SpecialService.getSysParameter(
        SysParameters.ReportsMenuAdvanceModeEnabled
      ).catch(
        () => ({ data: [] })
      )
      return (
        data
          ?.find((x) => x.name === SysParameters.ReportsMenuAdvanceModeEnabled)
          ?.staticValue?.toLowerCase() === 'true'
      )
    },
    async addToFavorites(item) {
      if (!this.menuAdvanceModeEnabled) {
        return
      }
      await addNavToFavorites(item.id)
      await this.refreshMenu()
    },
    async removeToFavorites(item) {
      if (!this.menuAdvanceModeEnabled) {
        return
      }
      await removeNavToFavorites(item.id)
      await this.refreshMenu()
    },
    async refreshMenu() {
      const userMenu = await getUserMenu()
      if (userMenu && userMenu.length) {
        this.setMenu(userMenu)
      }
    },
  },
  beforeRouteLeave(to, from, next) {
    this.setFilterText('')
    next()
  },
}
</script>
<style scoped>
::v-deep .dot.text-center > i {
  padding-right: 0px !important;
  font-size: 20pt !important;
  margin: 0px 6px !important;
}

.favorite-title::v-deep .tag-badge-info {
  background: linear-gradient(to right, white, #e2e2e2);
}

.favorite-title::v-deep .dot {
  background-color: #fff !important;
}

.favorite-title::v-deep .dot.text-center > i {
  font-size: 16px !important;
  margin: 5px !important;
  padding-right: 0 !important;
}

.menu-list-container {
  display: flex;
  flex-wrap: wrap;
  margin: 0 0 1rem;
}

.view-toggle {
  display: flex;
  border: 1px solid #e2e2e2;
  border-radius: 6px;
  overflow: hidden;
}

.view-toggle-btn {
  background: #f2f2f2;
  border: none;
  color: #3f5b60;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  height: 34px;
  width: 34px;
}

.view-toggle-btn.active {
  background: rgb(63, 91, 96);
  color: #fff;
}

#border {
  background: linear-gradient(to right, #4caf50 50%, rgba(255, 255, 255, 0) 0%);
  background-repeat: repeat-x;
  background-size: 10px 1px;
  height: 1px;
  margin: 2.5rem 1rem 0;
}
</style>
